import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '@contexts/auth/auth-context';
import { DevModeContext } from '@contexts/dev-mode/dev-mode-context';
import { UserContext } from '@contexts/user/user-context';
import { usePostHog } from '@posthog/react';
import { useFeatureFlagState } from '@posthog-app/use-feature-flag';

import { persistDevModeEligibility, readDevModeEligibility } from './dev-mode-eligibility';
import { persistDevModeChoice, readDevModeChoice } from './dev-mode-preference';

/**
 * Nightgraph's internal dev mode (B2B-2341): Copy ID / Copy object on the list cards, eleven
 * surfaces across offers, presets, invitations, floorplans and the events list.
 *
 * Two layers, both required. The PostHog flag decides who MAY have it — targeting lives in the
 * `b2b devs` cohort, so access is granted by editing the cohort, never this code. The switch
 * decides whether it is on RIGHT NOW for this browser, and it starts off: an always-visible icon
 * gets in the way of validating UI, which is why the flag alone is not the answer.
 *
 * The flag is read here once, never per card: with ~25 `AdmissionCard` instances on a page, a
 * per-card `useFeatureFlag` would be 25 subscriptions for one fact (the `companyClub` pattern).
 *
 * Eligibility survives impersonation (B2B-2345), which is where support work happens. It cannot be
 * asked for directly there: since B2B-2158 an impersonated session identifies in PostHog as the
 * customer, so `dev-mode` is evaluated against a person who is not in the cohort and comes back
 * false, and nothing a PostHog flag can key on survives the identity switch. So the last
 * non-impersonated session remembers the answer (`dev-mode-eligibility.ts`) and impersonated
 * sessions read that. Keying on `isImpersonated` itself would have been three lines and is
 * server-verified, but it grants dev mode to every super admin instead of to the cohort.
 * `isSuperAdmin` is not part of the gate for the mirror-image reason: impersonation swaps the
 * ACTIVE token, so `/context` answers as the impersonated user and a `flag && isSuperAdmin` gate
 * would switch dev mode off during exactly the support work it exists for.
 *
 * Neither layer is a security boundary. Both values are in the browser; a client-side gate hides,
 * it does not protect. Anything privileged hung off dev mode later must authorise server-side.
 */
export const DevModeProvider = ({ children }: { children: ReactNode }) => {
  const { isImpersonated } = useContext(AuthContext);
  const { userState } = useContext(UserContext);
  const posthog = usePostHog();
  const flag = useFeatureFlagState('dev-mode');
  // Seeded from storage, then kept in step with the flag below. `isImpersonated` flips to true a
  // moment BEFORE `changeUser`'s navigation lands, and `isAvailable` reads this state for that
  // window, so a value frozen at mount would answer wrongly there in both directions.
  const [isEligible, setIsEligible] = useState(readDevModeEligibility);
  const [isOn, setIsOn] = useState(readDevModeChoice);

  // While impersonating, the flag holds the customer's answer, so the remembered eligibility is the
  // only one worth asking. Outside impersonation the flag itself decides, fail-closed while it
  // loads so a slow `/flags` never flashes the icon on.
  const isAvailable = isImpersonated ? isEligible : flag ?? false;

  // The updater's argument, so two toggles batched into one render cannot both read the same stale
  // `isOn` and collapse into a single flip. Empty deps, so the memo below no longer invalidates on
  // every toggle.
  const toggleDevMode = useCallback(() => setIsOn((wasOn) => !wasOn), []);

  // Record eligibility only from a non-impersonated session, and only from a flag PostHog resolved
  // FOR THE PERSON WE THINK IS LOGGED IN. Each condition earns its place:
  //   - `flag !== undefined`: it is `undefined` until `/flags` answers, and taking that for a
  //     `false` would clear the marker on every page load, for good wherever PostHog is blocked or
  //     absent.
  //   - a `userState` whose id is PostHog's current `distinct_id`: an anonymous session resolves the
  //     flag against nobody and gets `false`, which is "no answer", not "not eligible" — acting on
  //     it wipes a cohort member's marker. That is the public event report page, and the load right
  //     after `signOut` or `resetAdminUser` call `posthog.reset()`. `AuthContext.isAuthenticated`
  //     cannot express this: it is seeded once from the access cookie and never updated, so it
  //     stays false for a whole tab recovered through the refresh flow, and it is forced true on
  //     the public report page — wrong in both directions. The identify in `auth-provider` guards
  //     itself with the same `get_distinct_id()` comparison.
  // Compared before writing, like the switch below, so nothing is planted in a browser whose user
  // was never eligible.
  //
  // What this does NOT close: posthog serves the previous answer until the reload triggered by an
  // identify returns, so the first pass after an identity change can still act on one stale
  // resolved value. It corrects itself within that round trip — handbook §18 §3 says so out loud.
  useEffect(() => {
    if (isImpersonated || flag === undefined) return;
    if (!userState || posthog?.get_distinct_id() !== userState.id) return;
    setIsEligible(flag);
    if (readDevModeEligibility() !== flag) persistDevModeEligibility(flag);
  }, [isImpersonated, flag, userState, posthog]);

  // Persisting lives here rather than in the toggle because `isOn` is then the single source of
  // truth: any future writer of the state gets its choice stored without having to remember to.
  // Compared first so the mount is not a write — this provider wraps the whole app, and an
  // unconditional effect would create the key in every user's browser for a six-person feature.
  useEffect(() => {
    if (readDevModeChoice() !== isOn) persistDevModeChoice(isOn);
  }, [isOn]);

  // Repo rule, and Sonar S6481: a fresh object here re-renders every consumer on every render of
  // a provider that wraps the whole app.
  const value = useMemo(
    () => ({ isAvailable, isOn, isDevMode: isAvailable && isOn, toggleDevMode }),
    [isAvailable, isOn, toggleDevMode]
  );

  return <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>;
};
